import React, {useEffect, useRef, useState} from 'react';
import {Button} from 'antd';
import DateRangeFilterItem from "../../../delivery/components/DateRangeFilterItem";
import ParcelFilterStyle from "../../../delivery/components/ParcelFilterStyle";
import {useWarehouseContext, WarehouseData} from "../../../../contexts/WarehouseContext";
import {DspType} from "../../../../graphql/types/DspType";
import {DriverType} from "../../../../graphql/types/AreaPlanningType";
import {useSearchParams} from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import { getLastWeekDateRange } from '../../../../utils/dateTimeUtils';

interface DepartureDrawerFilterProps {
    filters: any;
    setFilters: (filters: any) => void;
    handleFilter: () => void;
    handleResetFilters: () => void;
    dsps: DspType[];
    drivers: DriverType[];
    disabled?: boolean;
}

const DepartureDrawerFilter: React.FC<DepartureDrawerFilterProps> = ({
                                                                         filters,
                                                                         setFilters,
                                                                         handleFilter,
                                                                         handleResetFilters,
                                                                         dsps,
                                                                         drivers,
                                                                     }) => {
    const { t } = useTranslation();
    const { currentWarehouse } = useWarehouseContext();
    const [searchParams] = useSearchParams();
    const [warehouseOption, setWarehouseOption] = useState<{id: string, name: string}[]>([]);
    // - undefined：未初始化，默认最近一周
    // - null：用户手动清空（点击叉号），保持为空
    // 一旦用户动过时间（包含点叉号清空），在 reset/刷新前都不再自动清空默认时间
    const hasUserEditedTaskDateRef = useRef(false);

    const getEffectiveTaskDate = (value: any): [string | null, string | null] | null => {
        if (value === null) return null;
        const isValid = value &&
            Array.isArray(value) &&
            value.length === 2;
        return isValid ? (value as [string | null, string | null]) : getLastWeekDateRange('YYYY/MM/DD');
    };

    // 转换 WarehouseType 为选项格式
    const mapToWarehouseOption = (warehouse: WarehouseData) => ({
        id: String(warehouse.id || ''),
        name: warehouse.name || '',
    });

    // 初始化过滤器，只显示当前仓库
    useEffect(() => {
        const dspId = searchParams.get('dspId');
        const driverAccountId = searchParams.get('driverAccountId');

        setFilters({
            warehouseId: currentWarehouse?.id || '',
            dspId: dspId || '',
            driverAccountId: driverAccountId || '',
            // 默认日期不写入 URL/hash：保持 undefined
            taskDate: filters?.taskDate
        });
        
        // 只显示当前仓库，且不可编辑
        setWarehouseOption(currentWarehouse ? [mapToWarehouseOption(currentWarehouse)] : []);
    }, [currentWarehouse, searchParams]);

    // 处理DSP选择变化
    const handleDspChange = (selectedDspId: string) => {
        const shouldClearDefaultTaskDate = filters?.taskDate === undefined && !hasUserEditedTaskDateRef.current;
        setFilters({
            ...filters,
            dspId: selectedDspId,
            ...(shouldClearDefaultTaskDate ? { taskDate: null } : null),
        });
    };

    // 处理司机选择变化
    const handleDriverChange = (selectedDriverAccountId: string) => {
        const shouldClearDefaultTaskDate = filters?.taskDate === undefined && !hasUserEditedTaskDateRef.current;
        setFilters({
            ...filters,
            driverAccountId: selectedDriverAccountId,
            ...(shouldClearDefaultTaskDate ? { taskDate: null } : null),
        });
    };

    // 处理日期选择
    const handleTaskDateChange = (
        dates: [string | null, string | null] | null
    ) => {
        hasUserEditedTaskDateRef.current = true;
        setFilters({...filters, taskDate: dates});
    };

    const handleResetFiltersWithDefaultTime = () => {
        hasUserEditedTaskDateRef.current = false;
        handleResetFilters();
    };

    const labelTaskDate = String(t('wms.review.filters.taskDate'));
    const labelWhs = String(t('wms.review.filters.whs'));
    const labelDsp = String(t('wms.review.filters.dsp'));
    const labelDriver = String(t('wms.review.filters.driver'));

    const col1Labels = [labelTaskDate, labelDriver];
    const col2Labels = [labelWhs];
    const col3Labels = [labelDsp];

    return (
        <div className="mb-2 pl-0 py-2">
            {/* 使用 Grid 布局确保列对齐 */}
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <DateRangeFilterItem
                        label={labelTaskDate}
                        value={getEffectiveTaskDate(filters.taskDate)}
                        onChange={handleTaskDateChange}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelWhs}
                        options={warehouseOption.map(warehouse => ({
                            value: warehouse.id || '',
                            label: warehouse.name
                        })) || []}
                        value={filters.warehouseId || undefined}
                        onChange={() => {}} // 空函数，因为禁用选择
                        disabled={true} // 始终禁用
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col2Labels)}
                    />
                </div>
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelDsp}
                        options={dsps.map(dsp => ({
                            value: dsp.id || '',
                            label: dsp.name
                        })) || []}
                        value={filters.dspId || undefined}
                        onChange={handleDspChange}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col3Labels)}
                    />
                </div>
                {/* 第二行 - 司机筛选 */}
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={labelDriver}
                        options={drivers.filter(driver => driver).map(driver => ({
                            value: driver.accountId,
                            label: driver.name + ' (' + driver.phone + ')',
                        })) || []}
                        value={filters.driverAccountId || undefined}
                        onChange={handleDriverChange}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(col1Labels)}
                    />
                </div>
                <div className="min-w-0"></div>
                <div className="min-w-0"></div>
            </div>
            <div className="mb-1 flex justify-end gap-4 pl-0">
                <Button
                    type="primary"
                    onClick={handleFilter}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('wms.review.buttons.filter')}</span>
                </Button>
                <Button
                    type="default"
                    onClick={handleResetFiltersWithDefaultTime}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('wms.review.buttons.reset')}</span>
                </Button>
            </div>
        </div>
    );
};

export default DepartureDrawerFilter;